import { useEffect, useRef, useState } from 'react';
import { useAdobeClientId } from 'vl-common/src/hooks/useAdobeClientId';
import { useAdobeDCView } from 'vl-common/src/hooks/useAdobeDCView';
import styled from '@emotion/styled';
import { InfoModal } from 'vl-common/src/components/Modals';
import { getFileExtension } from 'vl-common/src/utils';
import { useQuery } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';

const PDFViewer = ({ url, fileName }: { url: string; fileName: string }) => {
  const containerDiv = useRef<HTMLDivElement>(null);
  const clientId = useAdobeClientId();
  const { adobeDC } = useAdobeDCView();

  const divId = 'document-preview';

  useEffect(() => {
    if (!containerDiv.current || !adobeDC) return;

    new adobeDC.View({ clientId, divId }).previewFile(
      { content: { location: { url } }, metaData: { fileName: fileName } },
      {
        enableFormFilling: false,
        showDownloadPDF: false,
        showPrintPDF: false,
        showAnnotationTools: false,
        showBookmarks: false,
        showThumbnails: false
      },
      containerDiv.current
    );
  }, [adobeDC, url, fileName]);

  return <div ref={containerDiv} id={divId} style={{ height: '100%' }} />;
};

const TxtPreview = ({ url }: { url: string }) => {
  const { data: content } = useQuery({
    queryKey: ['getFileContent', url],
    queryFn: async () => {
      const response = await fetch(url);
      return response.text();
    }
  });

  return <pre style={{ whiteSpace: 'pre-wrap', padding: '25px', height: '100%', overflow: 'auto' }}>{content}</pre>;
};

const ImageViewer = ({ url, fileName }: { url: string; fileName: string }) => {
  return <img alt={fileName} src={url} />;
};

const StyledInfoModal = styled(InfoModal)`
  width: 100% !important;
  height: 90%;

  &.image-info-modal {
    .ant-modal-body {
      height: 84%;
      text-align: center;

      & > img {
        object-fit: contain;
        height: 100%;
        width: 100%;
      }
    }
  }

  // mobile
  @media (max-width: 768px) {
    height: 65%;
  }

  // desktops
  @media (min-width: 1600px) {
    width: 85% !important;
    height: auto !important;
  }

  .ant-modal-content {
    display: flex;
    flex-direction: column;
    padding: 32px;
    height: calc(100vh - 64px);
  }

  .ant-modal-header {
    border-bottom: none;
    padding: 0;
    margin-bottom: 8px;
  }

  .ant-modal-body {
    padding: 0;
    margin-bottom: 24px;
    flex-grow: 1;
    overflow: hidden;
  }

  .ant-modal-footer {
    border: none !important;
    padding: 0;
  }
`;

const PreviewModal = ({
  fileName,
  patientGuid,
  isFactsheet,
  onClose
}: {
  fileName: string;
  patientGuid: string;
  isFactsheet?: boolean;
  onClose: () => void;
}) => {
  const { Api } = useApi();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isFactsheet) {
      setUrl(fileName);
    } else if (patientGuid && fileName) {
      Api.file
        .getPreSignedS3Url(patientGuid, { filename: fileName })
        .then((res: any) => {
          if (res?.data?.url) {
            setUrl(res.data.url);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to retrieve file URL', err);
        });
    }
  }, [Api, fileName, patientGuid, isFactsheet]);

  if (!url) return null;

  const fileExtension = getFileExtension(fileName);

  const renderViewer = () => {
    switch (fileExtension) {
      case 'pdf':
        return <PDFViewer url={url} fileName={fileName} />;
      case 'txt':
        return <TxtPreview url={url} />;
      default:
        return <ImageViewer fileName={fileName} url={url} />;
    }
  };

  return (
    <StyledInfoModal
      open
      title={`Preview of ${fileName}`}
      onCancel={onClose}
      onOk={onClose}
      closable={false}
      className={['pdf', 'txt'].includes(fileExtension) ? '' : 'image-info-modal'}
    >
      {renderViewer()}
    </StyledInfoModal>
  );
};

export default PreviewModal;
